import React, { useState, useMemo, useEffect } from 'react';
import { get_all_leads_api, update_lead_api } from '../controllers/dataController';
import Header from '../components/Header';
import { FaTimes, FaArrowLeft, FaComment, FaPhone, FaEnvelope, FaChevronDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/custom_ui/dropdown-menu.tsx";

const STATUS_OPTIONS = ['NEW', 'OPEN', 'CONNECTED', 'WORKING', 'PENDING', 'UNCLOSED', 'CLOSED'];

const getStatusClasses = (status) => {
    switch (status) {
        case 'NEW': return 'bg-blue-600 text-white';
        case 'OPEN': return 'bg-yellow-600 text-white';
        case 'CONNECTED': return 'bg-indigo-600 text-white';
        case 'WORKING': return 'bg-purple-600 text-white';
        case 'PENDING': return 'bg-orange-600 text-white';
        case 'UNCLOSED': return 'bg-red-600 text-white';
        case 'CLOSED': return 'bg-green-600 text-white';
        default: return 'bg-gray-500 text-white';
    }
};

const StatusDropdown = ({ currentStatus, leadId, onStatusChange }) => {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={`inline-flex justify-center items-center w-full rounded-md border border-gray-600 shadow-sm px-4 py-2 text-sm font-medium ${getStatusClasses(currentStatus)} transition-colors duration-150 outline-none`}
                >
                    {currentStatus}
                    <FaChevronDown className="ml-2 h-3 w-3" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-800 border-gray-700 z-[9999]">
                {STATUS_OPTIONS.map(status => (
                    <DropdownMenuItem
                        key={status}
                        onClick={() => onStatusChange(leadId, status)}
                        className={`cursor-pointer ${status === currentStatus ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-700'}`}
                        disabled={status === currentStatus}
                    >
                        {status}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const CommentModal = ({ lead, onClose, onSave }) => {
    const [comment, setComment] = useState(lead.admin_comment || '');

    const handleSave = () => {
        onSave(lead.id, comment);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4" onClick={onClose}>
            <div className="bg-gradient-card border border-border/50 shadow-shadow-card rounded-lg p-6 max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">Admin Comment</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <FaTimes size={20} />
                    </button>
                </div>
                <p className="text-sm text-gray-400 mb-4">Lead: {lead.name}</p>
                <textarea
                    className="w-full bg-background border border-gray-600 rounded p-3 text-white mb-4 focus:border-primary outline-none"
                    rows={5}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Enter notes about this lead..."
                />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">Cancel</button>
                    <button onClick={handleSave} className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90">Save Comment</button>
                </div>
            </div>
        </div>
    );
};

const LeadsPage = () => {
    const [leads, setLeads] = useState([]);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [selectedLead, setSelectedLead] = useState(null);
    const [showCommentModal, setShowCommentModal] = useState(false);

    const fetchLeads = () => {
        get_all_leads_api().then(response => {
            setLeads(response);
        }).catch(error => {
            console.error("Failed to get leads:", error);
        });
    };

    useEffect(() => {
        fetchLeads();
    }, []);

    const filteredLeads = useMemo(() => {
        if (filterStatus === 'ALL') return leads;
        return leads.filter(lead => lead.status === filterStatus);
    }, [leads, filterStatus]);

    const updateStatus = (id, newStatus) => {
        update_lead_api({ id, status: newStatus }).then(() => {
            fetchLeads(); // Refresh to update sorting/filtering if needed
        }).catch(error => {
            console.error("Failed to update lead status:", error);
        });
    };

    const updateComment = (id, comment) => {
        update_lead_api({ id, admin_comment: comment }).then(() => {
            fetchLeads();
        }).catch(error => {
            console.error("Failed to update comment:", error);
        });
    };

    const handleOpenComment = (lead) => {
        setSelectedLead(lead);
        setShowCommentModal(true);
    };

    const tableHeaders = ["ID", "Name", "Contact", "Message", "Status", "Date", "Admin Note"];

    return (
        <div className='bg-background min-h-screen flex flex-col'>
            <div className='sticky top-0 z-30 bg-background'>
                <Header />
            </div>
            <div className="text-gray-100 mx-4 md:mx-20 sm:px-8 pb-10 flex-grow flex flex-col">
                <div className="flex items-center mb-6 pt-4">
                    <Link to="/admin_dashboard" className="text-muted-foreground hover:text-primary mr-4">
                        <FaArrowLeft size={20} />
                    </Link>
                    <h1 className="text-3xl font-extrabold text-white">Leads Management</h1>
                </div>
                <p className="text-gray-400 pb-5">Track and manage potential client inquiries.</p>

                <div className="bg-gradient-card border border-border/50 shadow-shadow-card flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 p-4 rounded-lg gap-4">
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setFilterStatus('ALL')}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterStatus === 'ALL' ? 'bg-primary text-white shadow-lg' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                        >
                            All ({leads.length})
                        </button>
                        {STATUS_OPTIONS.map(status => {
                            const count = leads.filter(l => l.status === status).length;
                            return (
                                <button
                                    key={status}
                                    onClick={() => setFilterStatus(status)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterStatus === status ? 'bg-primary text-white shadow-lg' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                                >
                                    {status.charAt(0) + status.slice(1).toLowerCase()} ({count})
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-gradient-card border border-border/50 shadow-shadow-card rounded-lg shadow-xl overflow-x-auto flex-grow mb-10">
                    <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700 sticky top-0 z-20">
                            <tr>
                                {tableHeaders.map(header => (
                                    <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y bg-gradient-card border border-border/50 shadow-shadow-card">
                            {filteredLeads.length > 0 ? (
                                filteredLeads.map((lead) => (
                                    <tr key={lead.id} className="hover:bg-gray-700 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-300">#{lead.id}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium">{lead.name}</td>
                                        <td className="px-6 py-4 text-sm text-gray-300">
                                            <div className="flex flex-col gap-1">
                                                <a href={`mailto:${lead.email}`} className="flex items-center gap-2 hover:text-primary"><FaEnvelope size={12}/> {lead.email}</a>
                                                {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-2 hover:text-primary"><FaPhone size={12}/> {lead.phone}</a>}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-300 min-w-[200px]">
                                            <div className="line-clamp-2" title={lead.message}>{lead.message}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <StatusDropdown currentStatus={lead.status} leadId={lead.id} onStatusChange={updateStatus} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{new Date(lead.created_at).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button 
                                                onClick={() => handleOpenComment(lead)} 
                                                className={`flex items-center gap-2 px-3 py-1 rounded transition-colors ${lead.admin_comment ? 'bg-primary/20 text-primary hover:bg-primary/30' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                                            >
                                                <FaComment /> {lead.admin_comment ? 'Edit Note' : 'Add Note'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={tableHeaders.length} className="px-6 py-12 text-center text-gray-500">No leads found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCommentModal && selectedLead && (
                <CommentModal 
                    lead={selectedLead} 
                    onClose={() => setShowCommentModal(false)} 
                    onSave={updateComment} 
                />
            )}
        </div>
    );
};

export default LeadsPage;